'use client';
/**
 * LE CATALOGUE DU MATÉRIEL, POUR LES FORMULAIRES QUI EN CHOISISSENT UN ARTICLE.
 *
 * Né le 16/10/2026 pour la déclaration d'une non-conformité « réactif » (RQ :
 * « on choisit dans la liste, puis on short-liste le lot et les fournisseurs »).
 * Le produit est la clé d'entrée : c'est lui qui connaît ses lots (par
 * `produitCode`) et son fournisseur (au carnet) — l'écran n'a donc plus à
 * proposer les 200 lots du registre ni les 40 fournisseurs du carnet quand
 * l'écart porte sur un seul article.
 *
 * ⚠️ CHARGÉ UNE FOIS ET MIS EN CACHE AU NIVEAU DU MODULE, comme le carnet de
 * contacts (`ChoixCorrespondant`) : la déclaration et la correction de fiche
 * montent le même sélecteur, parfois en même temps.
 *
 * ⚠️ UN CATALOGUE VIDE OU INJOIGNABLE SE DIT, et ce n'est pas la même phrase :
 * « aucun produit » est un catalogue à remplir, « n'a pas pu être chargé » est
 * une panne. Un menu vide sans phrase se lit comme l'un ou l'autre au hasard.
 */
import React from 'react';
import { inputStyle, Selecteur } from '@/components/ui';

export interface ProduitBref {
  code: string; libelle: string; famille: string; fabricant: string;
  fournisseurCode: string; fournisseurNom: string; actif: boolean;
}

type Etat = { lignes: ProduitBref[]; charge: boolean; echec: boolean };

let cache: Etat | null = null;
let enVol: Promise<Etat> | null = null;

async function charger(): Promise<Etat> {
  if (cache) return cache;
  if (enVol) return enVol;
  enVol = fetch('/api/ged/ged/produits', { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const lignes = ((j?.lignes as Partial<ProduitBref>[]) || []).map((p) => ({
        code: String(p.code || ''), libelle: String(p.libelle || ''), famille: String(p.famille || ''),
        fabricant: String(p.fabricant || ''), fournisseurCode: String(p.fournisseurCode || ''),
        fournisseurNom: String(p.fournisseurNom || ''), actif: p.actif !== false,
      })).filter((p) => p.code);
      cache = { lignes, charge: true, echec: !j };
      return cache;
    })
    .catch(() => {
      /* L'échec n'est pas mis en cache : une coupure passagère ne doit pas
         rendre le champ inutilisable pour le reste de la séance. */
      enVol = null;
      return { lignes: [], charge: true, echec: true } as Etat;
    });
  return enVol;
}

/** À appeler après une écriture au catalogue, pour que les formulaires ouverts voient la nouvelle ligne. */
export function oublierCatalogue(): void { cache = null; enVol = null; }

export function useProduits(actif = true): Etat {
  const [etat, setEtat] = React.useState<Etat>(() => cache || { lignes: [], charge: false, echec: false });
  React.useEffect(() => {
    if (!actif || etat.charge) return;
    let vivant = true;
    void charger().then((e) => { if (vivant) setEtat(e); });
    return () => { vivant = false; };
  }, [actif, etat.charge]);
  return etat;
}

const FAMILLE: Record<string, string> = { reactif: 'Réactifs', controle: 'Contrôles', calibrant: 'Calibrants', consommable: 'Consommables' };
export const libelleProduit = (p: ProduitBref) => `${p.libelle}${p.fabricant ? ` · ${p.fabricant}` : ''}`;

export function ChoixProduit({ id, valeur, onChange, actif = true, aide, vide }: {
  id: string;
  valeur: string;
  onChange: (code: string) => void;
  /** Faux quand le champ n'est pas affiché : inutile d'appeler le serveur. */
  actif?: boolean;
  /** Phrase affichée sous le champ. Vide : la phrase par défaut. */
  aide?: React.ReactNode;
  /** Le libellé du choix vide. */
  vide?: string;
}) {
  const { lignes, charge, echec } = useProduits(actif);
  /* Un produit désactivé reste PROPOSÉ s'il est déjà choisi : le retirer de la
     liste effacerait le rattachement d'une NC ancienne au premier enregistrement. */
  const offerts = lignes.filter((p) => p.actif || p.code === valeur);
  const familles = [...new Set(offerts.map((p) => p.famille))];
  return (
    <>
      <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle}>
        <option value="">{vide || '— produit hors catalogue'}</option>
        {familles.map((f) => (
          <optgroup key={f} label={FAMILLE[f] || f}>
            {offerts.filter((p) => p.famille === f).map((p) => (
              <option key={p.code} value={p.code}>{libelleProduit(p)}{p.actif ? '' : ' (inactif)'}</option>
            ))}
          </optgroup>
        ))}
        {valeur && !offerts.some((p) => p.code === valeur) && charge && (
          <option value={valeur}>{valeur} — inconnu du catalogue</option>
        )}
      </Selecteur>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
        {echec
          ? 'Le catalogue des produits n’a pas pu être chargé — ce n’est pas « aucun produit ».'
          : charge && !lignes.length
            ? 'Le catalogue des produits est vide : référencez-y les produits depuis « Catalogue des produits ».'
            : aide || 'Choisi au catalogue, le produit restreint les lots et le fournisseur proposés à ceux qui sont les siens.'}
      </div>
    </>
  );
}
